import { Expose, Type } from '@nts/std/serialization';
import { OCCModel } from '../occ-model';
import { NmItemsTypeInspector, NmRootTypeInspector } from '../decorators/nm';
import { IdentityInterface, ModelInterface } from '@nts/std/interfaces';
import { BaseNMCollection } from './base-nm-collection';

/**
 * Base class per rappresentare
 * Una istanza di un aggregato per cui esiste una relazione N:M con un altro aggregato.
 * Per questo aggregato principale viene gestita la collezione contenente tutti gli altri aggregati della relazione N:M ad esso collegati
 */
export abstract class NmOCCModel<
    
        /**
         * Model che rappresenta l'estremità principale della relazione N:M (sarà utilizzato come discriminante per caricare la collection delle associazioni)
         */
        TRootDiscriminator extends ModelInterface<TIdentity>,
        
        /**
         * Identity del Model principale
         */
        TIdentity extends IdentityInterface,

        /**
         * Tipo dell'associazione N:M
         */
        TItem extends ModelInterface<TItemIdentity>,
        
        /**
         * Idenity dell'associazione N:M
         */
        TItemIdentity extends IdentityInterface,

        /**
         * Estremità opposta al discriminatore della relazione N:M
         */
        TOtherExternal extends ModelInterface<TOtherExternalIdentity>, 

        /**
         * Idenity dell'estremità opposta al discriminatore della relazione N:M
         */
        TOtherExternalIdentity extends IdentityInterface,

        /**
         * Tipo della collection di associazioni
         */
        TNMCollection extends BaseNMCollection<TItem, TItemIdentity, TRootDiscriminator, TIdentity, TOtherExternal, TOtherExternalIdentity>
    
    > extends OCCModel<TIdentity> {
        
    abstract discriminatorPropertyName: string;
    
    // tslint:disable-next-line: variable-name
    private _root: TRootDiscriminator;

    private _nmOccRootType: any;

    @Expose()
    @Type((options) => {
        return (options?.newObject as NmOCCModel<TRootDiscriminator, TIdentity, TItem, TItemIdentity, TOtherExternal, TOtherExternalIdentity, TNMCollection>)?._nmOccRootType;
    })
    get root(): TRootDiscriminator {
        return this.getValueForExternal<TRootDiscriminator>(
            () => this._root,
            'root',
            this._nmOccRootType
        );
    }

    set root(value: TRootDiscriminator) {
        this.setValueForExternal(() => {
            this._root = value;
        }, value, 'root');
    }
    
    // tslint:disable-next-line: variable-name
    private _items: TNMCollection;

    private _nmOccItemsType: any;

    @Expose()
    @Type((options) => {
        return (options?.newObject as NmOCCModel<TRootDiscriminator, TIdentity, TItem, TItemIdentity, TOtherExternal, TOtherExternalIdentity, TNMCollection>)?._nmOccItemsType;
    })
    get items(): TNMCollection {        
        return this.getValueForInternalCollectionWithDifferentIdentity<
        TNMCollection, TItem, TItemIdentity>(
            (value) => {
                this._items = value;
            },
            this._items,
            'items',
            this._nmOccItemsType
        );
    }

    set items(value: TNMCollection) {
        this.setValueForInternalCollectionWithDifferentIdentity<
        TNMCollection, TItem, TItemIdentity>(() => {
            this._items = value;
        }, value, 'items');
    }
    
    constructor() {
        super();

        this._nmOccRootType = NmRootTypeInspector.getValue(this);
        if (this._nmOccRootType === undefined) {
            throw new Error(
                `MetaData ${NmRootTypeInspector.META_DATA_KEY} not defined. You must use ${NmRootTypeInspector.DECORATOR_NAME} in ${this.constructor.name}.`
            );
        }

        this._nmOccItemsType = NmItemsTypeInspector.getValue(this);
        if (this._nmOccItemsType === undefined) {
            throw new Error(
                `MetaData ${NmItemsTypeInspector.META_DATA_KEY} not defined. You must use ${NmItemsTypeInspector.DECORATOR_NAME} in ${this.constructor.name}.`
            );
        }
    }
}
